/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";

import DatePickerComponent from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import {
    dayjsToDatePickerFormat,
    dayjsToDatePickerPlaceholder,
    getDefaultDateFormats,
} from "../scenes/Layers/fields/formatters";

interface DatePickerProps {
    value: Date | string | null; // SQL-friendly format (yyyy-mm-dd)
    displayFormat?: string; // EFQ-3884 — Day.js format for the visible input (defaults to tenant config)
    onChange: (value: Date | string | null) => void;
    [key: string]: any;
}

export default function DatePicker({
    value,
    displayFormat,
    onChange,
    className,
    ...props
}: DatePickerProps) {
    const [localState, setLocalState] = useState<Dayjs | null>(value ? dayjs(value) : null);

    // EFQ-3884 — resolve the visible input format from the tenant's configured
    // Day.js `date_format` (set via root config / ConfigProvider) so the
    // date-picker entry matches the display format (e.g. MM/DD/YYYY for US).
    const dayjsDisplayFormat = displayFormat || getDefaultDateFormats().date_format;
    const datePickerFormat = dayjsToDatePickerFormat(dayjsDisplayFormat);
    const datePickerPlaceholder = dayjsToDatePickerPlaceholder(dayjsDisplayFormat);

    useEffect(() => {
        setLocalState(value ? dayjs(value) : null);
    }, [value]);

    // EFQ-4209 — `react-datepicker` must receive a real `Date`. When handed a
    // `YYYY-MM-DD` string it falls back to `new Date(string)`, which the JS spec
    // parses as *UTC* midnight. West-of-UTC users (US) then saw the previous day
    // rendered in the input. `dayjs` parses a date-only string as local midnight,
    // so `.toDate()` keeps the calendar day the user picked in every timezone.
    const toSelectedDate = (date: Dayjs | null) => (date && date.isValid() ? date.toDate() : null);

    const handleChange = (newValue: string | Date | null) => {
        const newDate = dayjs(newValue);
        setLocalState(newDate.isValid() ? newDate : null);

        // Ensure the value passed to `onChange` is in SQL format (yyyy-mm-dd)
        const formattedSql = newDate.isValid() ? newDate.format("YYYY-MM-DD") : null;
        onChange(formattedSql);
    };

    return (
        <div
            className={className}
            css={css`
            .react-datepicker-wrapper {
                width: 100%;
            }

            .react-datepicker__input-container input {
                display: block;
                width: 100%;
                padding: 6px;
                border-radius: 4px;
                border: 1px solid #ccc;
                font-size: 16px;
                outline: none;
            }
                
            .react-datepicker__input-container input:focus {
                border-color: #2684ff;
                box-shadow: 0 0 0 1px #2684ff;
            }`}
        >
            <DatePickerComponent
                dateFormat={datePickerFormat}
                selected={toSelectedDate(localState)}
                onChange={handleChange}
                placeholderText={datePickerPlaceholder}
                {...props}
            />
        </div>
    );
}
